import { useState } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  AvatarGroup,
  Avatar,
  Typography,
  Chip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { AdminChatConversation, AdminChatMessage, AdminTableColumn, AdminTableAction } from '../../../types/admin';
import AdminTable from '../../admin/shared/AdminTable';
import ConversationViewer from './ConversationViewer';
import FlagModal from './FlagModal';
import { format, formatDistanceToNow } from 'date-fns';

interface ChatModerationProps {
  conversations: AdminChatConversation[];
  isLoading: boolean;
  onSearch: (query: string) => void;
  onFilterFlagged: (flagged: boolean | undefined) => void;
  onFlagConversation: (conversationId: string, reason: string) => Promise<{ success: boolean; error?: string }>;
  onGetConversationMessages: (conversationId: string) => AdminChatMessage[];
}

/**
 * ChatModeration - Chat messages moderation tab
 *
 * Features:
 * - Search conversations by participants
 * - Filter by flagged status
 * - View full conversation thread
 * - Flag conversations
 */
export default function ChatModeration({
  conversations,
  isLoading,
  onSearch,
  onFilterFlagged,
  onFlagConversation,
  onGetConversationMessages,
}: ChatModerationProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [flaggedFilter, setFlaggedFilter] = useState('all');
  const [selectedConversation, setSelectedConversation] = useState<AdminChatConversation | null>(null);
  const [conversationMessages, setConversationMessages] = useState<AdminChatMessage[]>([]);
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [conversationToFlag, setConversationToFlag] = useState<AdminChatConversation | null>(null);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    onSearch(query);
  };

  const handleFlaggedFilter = (flagged: string) => {
    setFlaggedFilter(flagged);
    onFilterFlagged(flagged === 'all' ? undefined : flagged === 'flagged');
  };

  const handleViewConversation = (conversation: AdminChatConversation) => {
    const messages = onGetConversationMessages(conversation.id);
    setConversationMessages(messages);
    setSelectedConversation(conversation);
  };

  const handleOpenFlagModal = (conversation: AdminChatConversation) => {
    setConversationToFlag(conversation);
    setFlagModalOpen(true);
  };

  const handleFlag = async (reason: string, _customReason?: string) => {
    if (conversationToFlag) {
      await onFlagConversation(conversationToFlag.id, reason);
      setFlagModalOpen(false);
      setConversationToFlag(null);
    }
    return { success: true };
  };

  const columns: AdminTableColumn<AdminChatConversation>[] = [
    {
      key: 'participants',
      label: 'Participants',
      render: (conversation) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AvatarGroup max={3}>
            {conversation.participants.map((participant, index) => (
              <Avatar
                key={index}
                src={participant.picture}
                alt={participant.name}
                sx={{ width: 32, height: 32 }}
              >
                {participant.name.charAt(0)}
              </Avatar>
            ))}
          </AvatarGroup>
          <Box>
            {conversation.participants.map((p, i) => (
              <Box key={i}>
                <Typography variant="body2" fontWeight="medium">
                  {p.name}
                  {p.role && (
                    <Chip
                      label={p.role}
                      size="small"
                      sx={{ ml: 1, height: 16, fontSize: '0.65rem' }}
                    />
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {p.email}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      width: 150,
      render: (conversation) => (
        <Chip
          label={conversation.type === 'agent_client' ? 'Agent-Client' : 'Group'}
          size="small"
          color={conversation.type === 'agent_client' ? 'primary' : 'secondary'}
          variant="outlined"
        />
      ),
    },
    {
      key: 'message_count',
      label: 'Messages',
      width: 100,
      render: (conversation) => (
        <Typography variant="body2">{conversation.message_count}</Typography>
      ),
    },
    {
      key: 'last_message',
      label: 'Last Message',
      render: (conversation) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 200,
          }}
        >
          {conversation.last_message}
        </Typography>
      ),
    },
    {
      key: 'last_activity',
      label: 'Last Activity',
      width: 150,
      render: (conversation) => (
        <Box>
          <Typography variant="body2">
            {format(new Date(conversation.last_activity), 'MMM d, yyyy')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDistanceToNow(new Date(conversation.last_activity), { addSuffix: true })}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'flagged',
      label: 'Status',
      width: 100,
      render: (conversation) => (
        conversation.flagged ? (
          <Chip
            label="Flagged"
            color="error"
            size="small"
          />
        ) : (
          <Chip label="Clean" color="default" size="small" variant="outlined" />
        )
      ),
    },
  ];

  const actions: AdminTableAction<AdminChatConversation>[] = [
    {
      label: 'View Conversation',
      icon: <VisibilityIcon />,
      onClick: handleViewConversation,
      color: 'primary',
    },
    {
      label: 'Flag Conversation',
      icon: <FlagOutlinedIcon />,
      onClick: handleOpenFlagModal,
      color: 'warning',
      disabled: (conversation) => conversation.flagged,
    },
  ];

  return (
    <Box>
      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            placeholder="Search by participant name or email..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Flagged Status</InputLabel>
            <Select
              value={flaggedFilter}
              label="Flagged Status"
              onChange={(e) => handleFlaggedFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="flagged">Flagged Only</MenuItem>
              <MenuItem value="clean">Clean Only</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary">
              {conversations.length} conversations
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Table */}
      <AdminTable
        columns={columns}
        data={conversations}
        actions={actions}
        isLoading={isLoading}
      />

      {/* Conversation Viewer Modal */}
      <ConversationViewer
        open={!!selectedConversation}
        conversation={selectedConversation}
        messages={conversationMessages}
        onClose={() => {
          setSelectedConversation(null);
          setConversationMessages([]);
        }}
      />

      {/* Flag Modal */}
      <FlagModal
        open={flagModalOpen}
        itemType="conversation"
        itemName={
          conversationToFlag
            ? conversationToFlag.participants.map((p) => p.name).join(', ')
            : ''
        }
        onClose={() => {
          setFlagModalOpen(false);
          setConversationToFlag(null);
        }}
        onFlag={handleFlag}
      />
    </Box>
  );
}
